import { Loading } from '@arcadiapower/shrike';
import { UserMachine } from '~/machines/user-creation';
import { UtilityMachine } from '~/machines/utility-selection';
import { MachineScreenStates, ScreenMap } from '~/machines/utils/types';
import { AccountNumberContainer } from '~/screens/account-number/account-number.container';
import { BillUploadContainer } from '~/screens/bill-upload/bill-upload.container';
import { BillingContainer } from '~/screens/billing/billing.container';
import { CreateAccountContainer } from '~/screens/create-account/create-account.container';
import { CredentialsContainer } from '~/screens/credentials/credentials.container';
import { CsUnavailableContainer } from '~/screens/cs-unavailable/cs-unavailable.container';
import { Friction, Unexpected } from '~/screens/errors';
import { LMIProofDocsUploadContainer } from '~/screens/lmi-proof-docs-upload/lmi-proof-docs-upload.container';
import { PaymentHoldContainer } from '~/screens/payment-hold/payment-hold.container';
import { SelectUtilityContainer } from '~/screens/select-utility/select-utility.container';
import { TelesalesComplete } from '~/screens/telesales-complete';
import { BillingMachine } from '../billing-selection';
import { EnrollmentMachine } from '../enrollment-selection';
import { UploadMachine } from '../upload';
import { TelesalesFunnelMachine } from './types';

export type TeleSalesScreenMap = {
  utility: ScreenMap<TelesalesFunnelMachine, UtilityMachine>;
  user: ScreenMap<TelesalesFunnelMachine, UserMachine>;
  upload: ScreenMap<TelesalesFunnelMachine, UploadMachine>;
  enrollment: ScreenMap<TelesalesFunnelMachine, EnrollmentMachine>;
  billing: ScreenMap<TelesalesFunnelMachine, BillingMachine>;
  getEnrollmentOptions: ScreenMap<TelesalesFunnelMachine>;
  fetchBillingExperienceOptions: ScreenMap<TelesalesFunnelMachine>;
  completed: ScreenMap<TelesalesFunnelMachine>;
  unexpected: ScreenMap<TelesalesFunnelMachine>;
  csUnavailable: ScreenMap<TelesalesFunnelMachine>;
};

export const telesalesScreenMap: Readonly<TeleSalesScreenMap> = {
  utility: {
    utility: SelectUtilityContainer,
    bouncer: () => <Loading />,
    friction: () => <Friction />,
    redirect: () => <Loading />,
    invalidProjectId: () => <CsUnavailableContainer />,
    completed: () => <Loading />,
  },
  user: {
    microLearning: () => <Loading />,
    user: ({ micro, overrideCopy }) => (
      <CreateAccountContainer
        micro={micro}
        overrideCopy={overrideCopy}
        overrideProps={{
          flags: { hideGoogleButton: true },
        }}
      />
    ),
    google: () => <Friction />,
    completed: () => <Loading />,
  },
  upload: {
    idle: LMIProofDocsUploadContainer,
    submitting: LMIProofDocsUploadContainer,
    completed: () => <Loading />,
  },
  enrollment: {
    utilityConnect: CredentialsContainer,
    accountNumber: AccountNumberContainer,
    billUpload: BillUploadContainer,
    fallBackAccountNumber: ({ micro, overrideCopy, funnel }) => (
      <AccountNumberContainer
        micro={micro}
        funnel={funnel}
        overrideCopy={overrideCopy}
        overrideProps={{
          error: micro.context.errors.fallBackAccountNumber,
          pending: micro.matches('fallBackAccountNumber.submitting'),
        }}
      />
    ),
    nyLmiZoneOptions: () => <Loading />,
    isInLmiZone: () => <Loading />,
    completed: () => <Loading />,
    ameren: () => <Loading />,
    amerenAuthTimeout: () => <Loading />,
  },
  billing: {
    fetching: () => <Loading />,
    payment: BillingContainer,
    hold: PaymentHoldContainer,
    completed: () => <Loading />,
  },
  fetchBillingExperienceOptions: () => <Loading />,
  completed: ({ funnel: { context, send } }) => (
    <TelesalesComplete
      context={context}
      resendEmailHandler={() => send({ type: 'SEND_EMAIL', key: 'ResumeAtTerms' })}
      onSubmitEditInformation={data => send({ type: 'UPDATED_USER', data })}
      emailResult={context.emailResult}
    />
  ),
  getEnrollmentOptions: () => <Loading />,
  unexpected: () => <Unexpected />,
  csUnavailable: () => <CsUnavailableContainer />,
};

// NB: do not consume - if this errors, a state is missing a corresponding screen in the map
export const _completenessCheck: Record<
  MachineScreenStates<TelesalesFunnelMachine>,
  any
> = telesalesScreenMap;
